import { DefaultTheme } from 'styled-components';

// Extend the DefaultTheme interface
declare module 'styled-components' {
  export interface DefaultTheme {
    mode: 'light' | 'dark';
    colors: {
      text: string;
      background: string;
      border: string;
      primary: string;
      primaryHover: string;
      secondary: string;
      error: string;
      disabled: string;
      textPrimary: string;
      textSecondary: string;
      surface: string;
      textMuted?: string;
      borderLight?: string;
      success?: string;
      warning?: string;
      info?: string;
    };
    spacing: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
    fontSize: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
    borderRadius: {
      sm: string;
      md: string;
      lg: string;
      full: string;
    };
    fontWeight: {
      normal: number;
      medium: number;
      semibold: number;
      bold: number;
    };
    transitions: {
      default: string;
      fast: string;
      slow: string;
    };
  }
}

export const lightTheme = {
  mode: 'light',
  colors: {
    text: '#2d3748',
    background: '#ffffff',
    border: '#e2e8f0',
    primary: '#4299e1',
    primaryHover: '#3182ce',
    secondary: '#718096',
    error: '#e53e3e',
    disabled: '#a0aec0',
    textPrimary: '#2d3748',
    textSecondary: '#718096',
    surface: '#f7fafc',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    full: '9999px',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  transitions: {
    default: 'all 0.2s ease-in-out',
    fast: 'all 0.1s ease-in-out',
    slow: 'all 0.3s ease-in-out',
  },
};

export const darkTheme = {
  mode: 'dark',
  colors: {
    text: '#e2e8f0',
    background: '#2d3748',
    border: '#4a5568',
    primary: '#4299e1',
    primaryHover: '#3182ce',
    secondary: '#a0aec0',
    error: '#e53e3e',
    disabled: '#718096',
    textPrimary: '#e2e8f0',
    textSecondary: '#a0aec0',
    surface: '#2d3748',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    full: '9999px',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  transitions: {
    default: 'all 0.2s ease-in-out',
    fast: 'all 0.1s ease-in-out',
    slow: 'all 0.3s ease-in-out',
  },
};

export type ThemeType = 'light' | 'dark';

// Type assertion to ensure the theme objects match the DefaultTheme interface
export const getTheme = (mode: ThemeType): DefaultTheme => {
  const theme = mode === 'dark' ? darkTheme : lightTheme;
  return theme as DefaultTheme;
};
